import React from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Avatar,
  useColorModeValue,
} from '@chakra-ui/react';
import { Message } from '../../services/chatService';
import MessageContent from './MessageContent';
import MessageActions from './MessageActions';

interface ChatMessageProps {
  message: Message;
  messageIndex: number;
  userBg?: string;
  assistantBg?: string;
  assistantColor?: string;
  onRegenerate?: (index: number) => void;
  onFeedback?: (index: number, feedback: 'up' | 'down') => void;
  searchQuery?: string;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  messageIndex,
  userBg = 'primary.500',
  assistantBg = 'gray.700',
  assistantColor = 'white',
  onRegenerate,
  onFeedback,
  searchQuery,
}) => {
  const isUser = message.role === 'user';

  // Function to highlight search text in plain text
  const highlightUserText = (text: string, query: string): React.ReactNode => {
    if (!query || !text) return text;

    const parts: React.ReactNode[] = [];
    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    let lastIndex = 0;
    let searchIndex = lowerText.indexOf(lowerQuery, lastIndex);
    let keyCounter = 0;

    while (searchIndex !== -1) {
      if (searchIndex > lastIndex) {
        parts.push(text.substring(lastIndex, searchIndex));
      }

      // Add highlighted match
      parts.push(
        <Box
          key={`highlight-${keyCounter++}`}
          as='span'
          className='search-highlight'
        >
          {text.substring(searchIndex, searchIndex + query.length)}
        </Box>
      );

      lastIndex = searchIndex + query.length;
      searchIndex = lowerText.indexOf(lowerQuery, lastIndex);
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  return (
    <HStack
      alignSelf={isUser ? 'flex-end' : 'flex-start'}
      spacing={2}
      maxW='85%'
      className='chat-message-container'
    >
      {!isUser && <Avatar size='sm' name='AI' bg='primary.500' color='white' />}
      <VStack spacing={0} align={isUser ? 'flex-end' : 'flex-start'}>
        <Box
          bg={isUser ? userBg : assistantBg}
          color={isUser ? 'white' : assistantColor}
          px={4}
          py={2}
          borderRadius='lg'
          boxShadow='md'
        >
          {isUser ? (
            <Text fontSize='sm' whiteSpace='pre-wrap'>
              {searchQuery
                ? highlightUserText(message.content, searchQuery)
                : message.content}
            </Text>
          ) : (
            <MessageContent
              content={message.content}
              searchQuery={searchQuery}
            />
          )}
          <Text
            fontSize='xs'
            color={
              isUser
                ? 'whiteAlpha.700'
                : useColorModeValue('gray.500', 'whiteAlpha.600')
            }
            mt={1}
          >
            {message.timestamp.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Text>
        </Box>

        {/* Message Actions */}
        <MessageActions
          content={message.content}
          messageIndex={messageIndex}
          isUser={isUser}
          onRegenerate={onRegenerate}
          onFeedback={onFeedback}
        />
      </VStack>
      {isUser && <Avatar size='sm' name='You' bg='gray.600' />}
    </HStack>
  );
};

export default ChatMessage;
